import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaCapsules, FaFilter, FaSearch } from "react-icons/fa";
import Header from "../../components/IntranetHeader";
import Footer from "../../components/Footer";
import DrugCategoryGuide from "../../components/DrugCategoryGuide";
import { loadDrugMatrix } from "../../lib/drugMatrix";

export default function DrugMatrix({ session, profile }) {
  const navigate = useNavigate();
  const [data, setData] = useState({ categories: [], labels: [], drugs: [] });
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [label, setLabel] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    loadDrugMatrix()
      .then((result) => { if (active) setData(result); })
      .catch((err) => { if (active) setError(err.message || "Could not load the Drug Matrix."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const results = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.drugs.filter((drug) => {
      const matchesSearch = !q || [
        drug.name,
        drug.alternative_name,
        drug.description,
        drug.indications,
        drug.category?.name,
        ...drug.labels.map((item) => item.name),
      ].filter(Boolean).some((value) => String(value).toLowerCase().includes(q));
      const matchesCategory = category === "all" || String(drug.category_id) === category;
      const matchesLabel = label === "all" || drug.labels.some((item) => item.id === label);
      return matchesSearch && matchesCategory && matchesLabel;
    });
  }, [data, search, category, label]);

  const hasFilters = Boolean(search || category !== "all" || label !== "all");

  return <>
    <Header session={session} profile={profile} />
    <main className="nhsuk-main-wrapper pguh-drug-matrix" id="main-content" tabIndex="-1">
      <div className="nhsuk-width-container pguh-drug-page-shell">
        <header className="pguh-drug-hero">
          <div className="pguh-drug-hero-copy">
            <span className="pguh-eyebrow">Clinical resource</span>
            <h1><FaCapsules /> Drug Matrix</h1>
            <p>Search medicines by name, category or administration label.</p>
          </div>
          <div className="pguh-drug-hero__notice">
            <strong>Internal reference</strong>
            <span>Use alongside RUHT policies, competency requirements and authorised prescribing guidance.</span>
          </div>
        </header>

        {error && <div className="nhsuk-error-summary"><h2 className="nhsuk-error-summary__title">Drug Matrix unavailable</h2><div className="nhsuk-error-summary__body"><p>{error}</p></div></div>}

        <DrugCategoryGuide />

        <section className="pguh-drug-search-panel" aria-label="Drug Matrix filters">
          <label className="pguh-drug-filter-field pguh-drug-search-panel__search">
            <span className="pguh-drug-filter-field__label"><FaSearch /> Search the Drug Matrix</span>
            <input className="nhsuk-input" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by drug name, alternative name or notes" />
          </label>
          <label className="pguh-drug-filter-field">
            <span className="pguh-drug-filter-field__label"><FaFilter /> Category</span>
            <select className="nhsuk-select" value={category} onChange={(event) => setCategory(event.target.value)}>
              <option value="all">All categories</option>
              {data.categories.filter((item) => item.active).map((item) => <option key={item.id} value={String(item.id)}>{item.name}</option>)}
            </select>
          </label>
          <label className="pguh-drug-filter-field">
            <span className="pguh-drug-filter-field__label"><FaFilter /> Who can administer</span>
            <select className="nhsuk-select" value={label} onChange={(event) => setLabel(event.target.value)}>
              <option value="all">All labels</option>
              {data.labels.filter((item) => item.active).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
        </section>

        <div className="pguh-drug-results-heading">
          <div><span className="pguh-eyebrow">Results</span><h2>{loading ? "Loading medicines…" : `${results.length} medicine${results.length === 1 ? "" : "s"}`}</h2></div>
          {hasFilters && <button className="nhsuk-button nhsuk-button--secondary pguh-drug-small-button" type="button" onClick={() => { setSearch(""); setCategory("all"); setLabel("all"); }}>Clear filters</button>}
        </div>

        {!loading && results.length === 0 ? <div className="pguh-drug-empty"><FaCapsules /><h3>No medicines found</h3><p>Try changing the search term or filter selection.</p></div> : <div className="pguh-drug-grid">
          {results.map((drug) => <article className="pguh-drug-card" key={drug.id}>
            <div className="pguh-drug-card__category">{drug.category?.name || "Uncategorised"}</div>
            <div className="pguh-drug-card__body">
              <h3>{drug.name}</h3>
              {drug.alternative_name && <p className="pguh-drug-card__alternative">Also known as: {drug.alternative_name}</p>}
              <p className="pguh-drug-card__summary">{drug.description || "No summary has been added yet."}</p>
              {drug.labels.length > 0 && <div className="pguh-drug-labels" aria-label="Administration labels">{drug.labels.filter((item) => item.active).map((item) => <span key={item.id}>{item.name}</span>)}</div>}
            </div>
            <div className="pguh-drug-card__footer"><button className="nhsuk-button nhsuk-button--secondary" type="button" onClick={() => navigate(`/intranet/drug-matrix/${drug.slug}`)}>View medicine</button></div>
          </article>)}
        </div>}
      </div>
    </main>
    <Footer />
  </>;
}
